import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, BookOpen, Play, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import CertificateDownload from './CertificateDownload';

const StageContent = ({ stage, onBack, onStartChallenge, userProgress, stages, onStage6Complete }) => {
  
  // Auto-complete Stage 6 when viewed (since it has no challenge)
  useEffect(() => {
    if (stage.id === 6 && userProgress && !userProgress.completedStages.includes(6)) {
      // Mark Stage 6 as completed
      if (onStage6Complete) {
        onStage6Complete();
      }
    }
  }, [stage.id, userProgress, onStage6Complete]);
  const renderContent = (item, index) => {
    switch (item.type) {
      case 'text':
        return (
          <p key={index} className="text-slate-700 leading-relaxed mb-4 text-lg">
            {item.value}
          </p>
        );
      case 'list':
        return (
          <ul key={index} className="space-y-3 mb-6 ml-4">
            {item.value.map((listItem, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-700 text-lg">
                <CheckCircle2 className="w-5 h-5 text-purple-700 mt-1 flex-shrink-0" />
                <span>{listItem}</span>
              </li>
            ))}
          </ul>
        );
      case 'highlight':
        return (
          <Alert key={index} className="mb-6 border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-blue-50">
            <BookOpen className="h-5 w-5 text-purple-700" />
            <AlertDescription className="text-slate-800 font-semibold text-lg ml-2">
              {item.value}
            </AlertDescription>
          </Alert>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={onBack}
            className="mb-4 hover:bg-slate-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <Card className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-blue-50">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-purple-700 text-white flex items-center justify-center font-bold text-xl">
                  {stage.id}
                </div>
                <div>
                  <CardTitle className="text-3xl text-purple-900">{stage.title}</CardTitle>
                  <CardDescription className="text-slate-700 mt-1 text-base">
                    {stage.subtitle}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>

        {/* Content */}
        <Card className="mb-8 border-2 border-slate-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100">
            <CardTitle className="text-2xl text-slate-900 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-purple-700" />
              Learning Material
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-8 pb-8">
            <p className="text-slate-600 mb-8 text-lg leading-relaxed">{stage.description}</p>
            <div className="space-y-4">
              {stage.content.map((item, index) => renderContent(item, index))}
            </div>
          </CardContent>
        </Card>

        {/* Action Button or Certificate Download */}
        {stage.id < 6 && (
          <Card className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-blue-50">
            <CardContent className="pt-6 pb-6">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  Ready to Test Your Knowledge?
                </h3>
                <p className="text-slate-600 mb-6 text-base">
                  Complete the challenge to unlock the next stage of your journey.
                </p>
                <Button
                  onClick={onStartChallenge}
                  className="bg-purple-700 hover:bg-purple-800 text-white px-8 py-6 text-lg transition-colors"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Start Challenge
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {stage.id === 6 && userProgress && stages && (
          <CertificateDownload userProgress={userProgress} stages={stages} />
        )}
      </div>
    </div>
  );
};

export default StageContent;